import { BaJwt, OiamEvents } from './index.js';
/**
 * Class which answers with a token, which is valid enough (at least for 30s) for the next API call.
 * If the input token fulfills this requirement, it is synchronously returned.
 * If the token is not valid enough, a token refresh is initiated and the refreshed token is
 * (asynchronously) returned.
 */
export class TokenEnsurer {
    constructor() {
        this.cachedToken = undefined;
        /**
         * Checks a given token for validity and responds either with this token or a fresh one.
         * Must only be called when the user is logged after the eService has received a JWT
         * in a oiamTokenEvent for the first time.
         * If called before oiamSetConfigEvent with an empty input, this method will wait until
         * oiam-oauth-wc sends out its first oiamTokenEvent. If no oiamSetConfigEvent is sent
         * by the eService (which is a bug in itself), this method will wait indefinitely.
         * Internally, if needed, this method triggers a SendToken event and waits for the resulting
         * Token event and returns that.
         *
         * @param t an input JWT which is checked for validity and directly returned, if valid long enough.
         *
         * @returns a JWT which is valid and won't expire within the next 30s. If not logged in, returns ''
         * (but you shouldn't try call protected APIs in this state anyway!).
         */
        this.ensureValidToken = async (t) => {
            const b = t instanceof BaJwt ? t : new BaJwt(t);
            if (TokenEnsurer.validLongEnough(b)) {
                this.cachedToken = b;
                return b.raw;
            }
            if (TokenEnsurer.validLongEnough(this.cachedToken)) {
                return this.cachedToken.raw;
            }
            return new Promise((resolve) => {
                // we assume that newly sent tokens are valid enough
                window.self.addEventListener(OiamEvents.TOKEN, (e) => {
                    const raw = e.detail.jwt;
                    this.update(raw);
                    resolve(raw);
                }, { once: true });
                const sendTokenEvent = new CustomEvent(OiamEvents.SENDTOKEN);
                window.self.dispatchEvent(sendTokenEvent);
            });
        };
    }
    /**
     * Updates the cached token to be able to answer faster on the next call of ensureValidToken.
     * If t is not valid or empty, the cache is emptied.
     * @param t the new token, either already parsed or as a raw string
     */
    update(t) {
        const b = t instanceof BaJwt ? t : new BaJwt(t);
        if (TokenEnsurer.validLongEnough(b)) {
            this.cachedToken = b;
        }
        else {
            this.cachedToken = undefined;
        }
    }
    static validLongEnough(t) {
        return !!t && t.expirationDate * 1000 > new Date().getTime() + 30000;
    }
}
